import { useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { enum_api_uri } from "../utils/enum";
import axiosInstance from "./axiosInstance";

// 모닝택배 입고지 관리 (입고지명 / 담당기사)
export interface Warehouse {
    warehouse_no: number;
    port_name: string;
    name: string;
    post: string;
    address1: string;
    address2: string;
    // 기본 담당기사 (기사 관리에서 선택, 1명)
    driver_no?: number;
    driver_name?: string;
    // 목록 기사 컬럼 — 기본 담당기사명(조인 결과).
    driver_names?: string;
}

export interface WarehouseListResponse {
    limit: number;
    current_page: number;
    last_page: number;
    total_count: number;
    data_list: Warehouse[];
}

interface WarehouseListRequest {
    searchtxt?: string;
    page?: number;
    limit?: number | string;
}

interface WarehouseBody {
    port_name: string;
    name: string;
    post: string;
    address1: string;
    address2: string;
    // 기본 담당기사 PK (없으면 빈 문자열)
    driver_no: string;
}

export const useGetConsoleWarehouses = (params: WarehouseListRequest): UseQueryResult<WarehouseListResponse, Error> => {
    return useQuery({
        queryKey: ["useGetConsoleWarehouses", params.searchtxt, params.page, params.limit],
        queryFn: async () => {
            const response = await axiosInstance.get(enum_api_uri.get_console_warehouses, { params });
            return response.data.data;
        },
    });
};

export const useGetConsoleWarehouseDetail = (warehouse_no: string): UseQueryResult<Warehouse, Error> => {
    const url = enum_api_uri.get_console_warehouse_detail.replace(":warehouse_no", warehouse_no);
    return useQuery({
        queryKey: ["useGetConsoleWarehouseDetail", warehouse_no],
        queryFn: async () => {
            const response = await axiosInstance.get(url);
            return response.data.data;
        },
        enabled: !!warehouse_no,
    });
};

export const usePostConsoleWarehouse = (): UseMutationResult<any, Error, WarehouseBody> => {
    return useMutation({
        mutationFn: async (body: WarehouseBody) => {
            const response = await axiosInstance.post(enum_api_uri.post_console_warehouse, body);
            return response.data;
        },
    });
};

export const usePutConsoleWarehouse = (): UseMutationResult<any, Error, WarehouseBody & { warehouse_no: string }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ warehouse_no, ...body }: WarehouseBody & { warehouse_no: string }) => {
            const url = enum_api_uri.put_console_warehouse.replace(":warehouse_no", warehouse_no);
            const response = await axiosInstance.put(url, body);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["useGetConsoleWarehouseDetail"] });
        },
    });
};

export const useDeleteConsoleWarehouse = (
    onSuccessCallback?: () => void,
): UseMutationResult<any, Error, { warehouse_no: number[] }> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (body: { warehouse_no: number[] }) => {
            const response = await axiosInstance.delete(enum_api_uri.delete_console_warehouse, { data: body });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["useGetConsoleWarehouses"] });
            onSuccessCallback?.();
        },
    });
};
